<template>
  <modal-vue
    :modal-active="modelValue"
    has-header-border
    has-footer-border
    :max-width="modalMaxWidth"
    class="send-record-modal"
    @close="handleClose"
  >
    <template #header>
      <div class="w-full text-left">
        <h2 class="text-xl font-bold text-textPrimary">{{ title }}</h2>
        <div v-if="currentStep <= SEND_RECORD_STEP.REVIEW" class="flex items-center gap-2 mt-2">
          <span
            v-for="step in stepperSteps"
            :key="step.value"
            class="text-xs"
            :class="step.value === currentStep ? 'text-primary30 font-bold' : 'text-neutral50'"
            :data-testid="`send-record-stepper-${step.value}`"
          >
            {{ step.value }} {{ step.label }}
          </span>
        </div>
      </div>
    </template>

    <StepCategory
      v-if="currentStep === SEND_RECORD_STEP.CATEGORY"
      :station="originStation"
      :category="category"
      :sheet-monitoring-type="sheetMonitoringType"
      :activity-subtype="activitySubtype"
      :sheet-blocked-reason="sheetBlockedReason"
      @update:category="category = $event"
      @update:sheet-monitoring-type="sheetMonitoringType = $event"
      @update:activity-subtype="activitySubtype = $event"
    />

    <StepStations
      v-else-if="currentStep === SEND_RECORD_STEP.STATIONS"
      :origin="originStation"
      :other-stations="otherStations"
      :selected="selectedDoctorStationIds"
      :loading="candidatesLoading"
      :error="candidatesError"
      @update:selected="setSelected"
      @retry="loadCandidates"
    />

    <StepTypeAndFiles
      v-else-if="currentStep === SEND_RECORD_STEP.TYPE_AND_FILES"
      :activity-type="activityType"
      :description="description"
      :files="files"
      :file-errors="fileErrors"
      :self-check="selfCheck"
      :accepted-activity-types="acceptedActivityTypes"
      :show-admin-adjustment="isAdminAdjustmentOffered"
      :formatted-date="formattedReferenceDate"
      :station-count="selectedDoctorStationIds.length"
      @update:activity-type="activityType = $event"
      @update:description="description = $event"
      @update:self-check="selfCheck = $event"
      @update:file-errors="fileErrors = $event"
      :active-index="attachmentIndex"
      @add-files="files = [...files, ...$event]"
      @remove-file="files = files.filter((_, index) => index !== $event)"
      @update:active-index="attachmentIndex = $event"
    />

    <template v-else-if="currentStep === SEND_RECORD_STEP.EQUIVALENCE">
      <AdminAdjustmentForm
        v-if="isAdminAdjustment"
        :item="prefill[0] ?? null"
        :line="lines[0]"
        :description="description"
        @update:line="lines = [$event]"
        @update:description="description = $event"
      />
      <!--
        Balão ⑧ / Q6: com os dois horários preenchidos, Enter avança a etapa. Quem
        digita 0702 e aperta Enter não precisa largar o teclado para achar "Próximo".
        O atalho é SÓ da equivalência: no Ajuste administrativo o passo é um
        formulário com textarea, onde Enter é quebra de linha.
      -->
      <div data-testid="send-record-equivalence-wrapper" @keydown.enter="handleEquivalenceEnter">
        <StepEquivalence
          :prefill="prefill"
          :lines="lines"
          :files="files"
          :checkin-late-tolerance-minutes="checkinLateToleranceMinutes"
          :checkout-early-tolerance-minutes="checkoutEarlyToleranceMinutes"
          :active-index="attachmentIndex"
          @update:lines="lines = $event"
          @update:active-index="attachmentIndex = $event"
        />
      </div>
    </template>

    <StepReview
      v-else-if="currentStep === SEND_RECORD_STEP.REVIEW"
      :activity-type="activityType"
      :description="description"
      :files="files"
      :lines="lines"
      :prefill="prefill"
      :tolerance-texts="toleranceTexts"
      :doctor-line="doctorLine"
      :truth-declaration="truthDeclaration"
      :error-code="errorCode"
      :error-message="errorMessage"
      :active-index="attachmentIndex"
      @update:truth-declaration="truthDeclaration = $event"
      @update:active-index="attachmentIndex = $event"
      @open-queue="openQueue"
    />

    <!-- Tela de sucesso: lista os N registros criados, um por plantão (balão ㉖). -->
    <div v-else class="flex flex-col gap-y-3 text-left" data-testid="send-record-success">
      <p class="text-sm font-bold text-textPrimary">
        Registro enviado — entrou na fila de auditoria
      </p>
      <p class="text-xs text-neutral50">
        {{ createdRecords.length }}
        {{ createdRecords.length === 1 ? 'registro criado' : 'registros criados' }} a partir deste
        envio, um por plantão, com os mesmos anexos.
      </p>
      <div
        v-for="record in createdRecords"
        :key="record.containerId"
        class="flex items-center justify-between rounded border border-gray-200 px-3 py-2"
      >
        <span class="text-sm font-bold text-textPrimary">#A-{{ record.containerId }}</span>
        <span class="text-xs text-neutral50">{{ describeCreated(record) }}</span>
        <span
          class="text-xs font-bold rounded-full bg-blue-100 text-blue-800 px-2 py-0.5"
          data-testid="send-record-success-badge"
        >
          Aguardando auditoria
        </span>
      </div>
      <ul
        v-if="warnings.length"
        class="rounded border border-yellow-200 bg-yellow-50 px-3 py-2 text-xs text-yellow-900"
        data-testid="send-record-success-warnings"
      >
        <li v-for="(warning, index) in warnings" :key="index">{{ warning.message }}</li>
      </ul>
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full gap-2">
        <template v-if="currentStep === SEND_RECORD_STEP.SUCCESS">
          <OutlineButton
            text="Ver na fila de auditoria"
            width="auto"
            height="40px"
            data-testid="send-record-go-to-queue"
            @click="openQueue"
          />
          <FilledButton
            text="Fechar"
            width="auto"
            height="40px"
            color="primary30"
            data-testid="send-record-close-button"
            @click="handleClose"
          />
        </template>

        <template v-else>
          <OutlineButton
            :text="currentStep === SEND_RECORD_STEP.CATEGORY ? 'Cancelar' : 'Voltar'"
            width="auto"
            height="40px"
            data-testid="send-record-back"
            @click="goBack"
          />

          <span
            v-if="currentStep === SEND_RECORD_STEP.EQUIVALENCE"
            class="hidden md:flex items-center gap-1 text-xs text-neutral50"
            data-testid="send-record-enter-hint"
          >
            [Enter] avança quando os dois horários estiverem preenchidos
          </span>

          <FilledButton
            v-if="currentStep === SEND_RECORD_STEP.REVIEW"
            :text="isSaving ? 'Enviando...' : 'Enviar para auditoria'"
            width="auto"
            height="40px"
            color="primary30"
            :disabled="!truthDeclaration || isSaving"
            :loading="isSaving"
            data-testid="send-record-submit"
            @click="submit"
          />

          <FilledButton
            v-else
            :text="nextLabel"
            width="auto"
            height="40px"
            color="primary30"
            :disabled="isNextDisabled"
            data-testid="send-record-next"
            @click="goNext"
          />
        </template>
      </div>
    </template>
  </modal-vue>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import Network from '@/Network'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import PATHS from '@/router/paths'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import {
  ActivityType,
  ContainerKind,
  CoveredLegs,
  DSAttendanceMonitoringType
} from '@/constants/ds-attendance.constants'
import { isTimeoutError, getApiErrorMessage } from '@/utils/apiError'
import { useErrorToast } from '@/composables/useErrorToast'
import type {
  ActivityRecordCreatedContainer,
  ActivityRecordWarning
} from '@/Network/Services/DsAttendanceService'
import StepCategory from './StepCategory.vue'
import StepStations from './StepStations.vue'
import StepTypeAndFiles from './StepTypeAndFiles.vue'
import StepEquivalence from './StepEquivalence.vue'
import StepReview from './StepReview.vue'
import AdminAdjustmentForm from './AdminAdjustmentForm.vue'
import {
  buildActivitySelfCheckPayload,
  emptyActivitySelfCheckState,
  type ActivitySelfCheckState
} from './activity-self-check'
import {
  ACTIVITY_ERROR_MESSAGES,
  ACTIVITY_TIMEOUT_MESSAGE,
  SEND_RECORD_STEP,
  SEND_RECORD_STEP_LABELS,
  buildLineFromPrefill,
  buildToleranceWarnings,
  candidateToStationOption,
  isDescriptionLongEnough,
  isMissingDeclaredTime,
  isSheetCategoryBlocked,
  SHEET_ALREADY_SUBMITTED_REASON,
  SHEET_BLOCKED_BY_APP_POLICY_REASON,
  isoToTimeInput,
  normalizePrefillItem,
  prefillFromStationOption,
  requiresAttachment,
  requiresDescriptionOnTypeStep,
  type ActivitySubtype,
  type CreatedActivityRecord,
  type NormalizedPrefillItem,
  type RedirectToSheetPayload,
  type SendRecordCategory,
  type SendRecordContext,
  type SendRecordLine,
  type SendRecordStationOption
} from './types'

/**
 * M2 — modal "Enviar registro", 5 passos (§2.1). Aberto pelo menu flutuante da
 * vaga (M1) e pelo dropdown "Enviar registro ▾" da fila unificada (M3).
 *
 * O passo 1 é uma bifurcação: "Registro de Plantão" NÃO tem fluxo novo — o
 * modal fecha e quem abriu reabre o `AttendanceUploadModal` que já existe, com
 * os campos da vaga preenchidos e bloqueados. Zero backend novo.
 *
 * Tolerância a backend antigo: a F2 web deploya antes da F2 backend. Sem a rota
 * de prefill, as linhas nascem do que a vaga já sabe e a tela segue funcionando.
 */

const props = defineProps<{
  modelValue: boolean
  context?: SendRecordContext | null
  /**
   * Atalhos para quem só tem os ids (FAB de M1, menu da fila). Quando vêm,
   * completam o `context` — é o mesmo plantão, escrito de duas formas.
   */
  doctorStationId?: number
  scheduleId?: number
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'redirect-to-sheet': [payload: RedirectToSheetPayload]
  /** `saved` é o nome do plano; `submitted` é o alias pedido pelos chamadores. */
  saved: [records: CreatedActivityRecord[]]
  submitted: [records: CreatedActivityRecord[]]
  'open-queue': []
}>()

const router = useRouter()
const { notifyError } = useErrorToast()

const currentStep = ref<number>(SEND_RECORD_STEP.CATEGORY)
const category = ref<SendRecordCategory | null>(null)
const sheetMonitoringType = ref<DSAttendanceMonitoringType | null>(null)
const activitySubtype = ref<ActivitySubtype>('JORNADA')
const selectedDoctorStationIds = ref<number[]>([])
const activityType = ref<ActivityType | null>(null)
const description = ref('')
const files = ref<File[]>([])
const fileErrors = ref<string[]>([])
const selfCheck = ref<ActivitySelfCheckState>(emptyActivitySelfCheckState())
const prefill = ref<NormalizedPrefillItem[]>([])
const lines = ref<SendRecordLine[]>([])
const truthDeclaration = ref(false)
const isSaving = ref(false)
const errorCode = ref<string | null>(null)
const errorMessage = ref<string | null>(null)
const warnings = ref<ActivityRecordWarning[]>([])
const createdRecords = ref<CreatedActivityRecord[]>([])
const idempotencyKey = ref('')
/**
 * Anexo em exibição, compartilhado pelas etapas 3, 4 e 5: quem escolheu o segundo
 * anexo para responder o checklist não deve reencontrar o primeiro na equivalência.
 */
const attachmentIndex = ref(0)

const stepperSteps = Object.entries(SEND_RECORD_STEP_LABELS).map(([value, label]) => ({
  value: Number(value),
  label
}))

/**
 * M6 — as etapas 4 e 5 ganharam o painel do anexo à direita (340px) e precisam de
 * largura para as duas colunas. As demais seguem no modal estreito de sempre.
 */
const modalMaxWidth = computed(() =>
  currentStep.value === SEND_RECORD_STEP.EQUIVALENCE ||
  currentStep.value === SEND_RECORD_STEP.REVIEW
    ? 'max-w-[980px]'
    : 'max-w-[640px]'
)

/* ----------------------------------------------------------------------- *
 * Contexto
 * ----------------------------------------------------------------------- */

const resolvedDoctorStationId = computed(
  () => props.doctorStationId ?? props.context?.doctorStationId ?? 0
)
const resolvedScheduleId = computed(() => props.scheduleId ?? props.context?.scheduleId ?? 0)

const originStation = computed<SendRecordStationOption>(() => ({
  ...(props.context?.station ?? {}),
  // O id da vaga manda: um `station` desatualizado não pode trocar o plantão.
  doctorStationId: resolvedDoctorStationId.value || (props.context?.station?.doctorStationId ?? 0)
}))

/**
 * BF16 — candidatos a "Adicionar outros plantões" (passo 2), buscados pelo
 * PRÓPRIO modal via `getActivityRecordCandidates`. Substitui o
 * `SendRecordContext.otherStations` (dead code: WF9 confirmou que nenhum
 * chamador nunca o populava). A lista é filtrada para excluir:
 * - a vaga de origem (mesmo que a rota a devolva)
 * - candidatos de escalas diferentes da origem (defesa contra falha de
 *   backend: o POST rejeita com `STATION_OUT_OF_SCOPE` qualquer plantão fora
 *   da escala informada, então um candidato de outra escala nunca pode
 *   chegar a ser selecionável aqui)
 */
const candidateStations = ref<SendRecordStationOption[]>([])
const candidatesLoading = ref(false)
const candidatesError = ref<string | null>(null)

const otherStations = computed(() =>
  candidateStations.value.filter(
    (option) =>
      option.doctorStationId !== resolvedDoctorStationId.value &&
      (option.scheduleId == null || option.scheduleId === resolvedScheduleId.value)
  )
)

/**
 * Busca os candidatos ao entrar no passo 2. Tolerante a erro real (mensagem +
 * retry no próprio passo) e a rota ausente (lista vazia — `Network` já resolve
 * esse caso, ver `getActivityRecordCandidates`).
 */
async function loadCandidates(): Promise<void> {
  const doctorStationId = resolvedDoctorStationId.value
  if (!doctorStationId) {
    candidateStations.value = []
    return
  }

  candidatesLoading.value = true
  candidatesError.value = null
  try {
    const stations = await Network.dsAttendance.getActivityRecordCandidates(doctorStationId, 30)
    candidateStations.value = stations.map(candidateToStationOption)
  } catch (error) {
    candidateStations.value = []
    candidatesError.value = getApiErrorMessage(
      error,
      'Não foi possível buscar outros plantões deste médico.'
    )
  } finally {
    candidatesLoading.value = false
  }
}

/**
 * Catálogo aceito pela política. Vazio (ou ausente) significa "política
 * desconhecida" — e desabilitar tudo travaria a tela. Quem valida de verdade é
 * o backend (V4); aqui a lista só evita o clique que ele já negaria.
 */
const acceptedActivityTypes = computed<ActivityType[]>(
  () => props.context?.acceptedActivityTypes ?? prefill.value[0]?.acceptedActivityTypes ?? []
)

const checkinLateToleranceMinutes = computed(
  () => props.context?.checkinLateToleranceMinutes ?? null
)
const checkoutEarlyToleranceMinutes = computed(
  () => props.context?.checkoutEarlyToleranceMinutes ?? null
)

/**
 * "Ajuste administrativo" é do perfil de backoffice/auditoria (§1.3 + Q4): quem
 * decide é a permissão `attendance_basic_receiving`, a mesma de quem audita.
 *
 * `isBackofficeAudit` do chamador é restrição ADICIONAL, nunca override — quem
 * monta o contexto pode esconder o tipo, jamais concedê-lo a quem não o tem.
 */
const hasAdminAdjustmentPermission = computed(() => {
  try {
    return usePermissionsStore().checkAnyRessources({
      permission: AuthorizationPermissions.AttendanceBasicReceiving,
      resourceType: AuthorizationResourcesAndSubjects.DsAttendance
    })
  } catch (error) {
    // fallback intencional: sem permissões carregadas a UI só esconde a opção de ajuste
    // administrativo (fail-closed); os demais tipos de registro seguem disponíveis.
    console.debug('[send-record] permissões indisponíveis; ajuste administrativo oculto', error)
    return false
  }
})

const canUseAdminAdjustment = computed(
  () => hasAdminAdjustmentPermission.value && props.context?.isBackofficeAudit !== false
)

/**
 * §2.6 — o tipo só existe quando a política da escala o aceita, e ele NÃO está
 * no default. Catálogo vazio (política desconhecida / backend F2 fora) portanto
 * não libera: é o backend que validaria (V4) e ele recusaria.
 *
 * C7 — a variação compacta de M2-2 é de UM plantão: com a multi-seleção ela só
 * enxergaria a primeira linha e as demais viajariam com valores nunca vistos.
 */
const isAdminAdjustmentOffered = computed(
  () =>
    canUseAdminAdjustment.value &&
    acceptedActivityTypes.value.includes(ActivityType.ADMIN_ADJUSTMENT)
)

const isAdminAdjustment = computed(() => activityType.value === ActivityType.ADMIN_ADJUSTMENT)

/**
 * A política vigente da escala não aceita folha (modalidade MINHA_ESCALA_APP, ou
 * sem regra de folha na data): o passo 1 desabilita "Registro de Plantão" com o
 * motivo, em vez de deixar o envio morrer no backend depois do upload.
 */
const sheetAlreadySubmitted = ref<boolean | null>(null)

const sheetBlockedReason = computed(() => {
  const blockedByPolicy = isSheetCategoryBlocked({
    monitoringType: props.context?.policyMonitoringType,
    hasSheetRuleInEffect: props.context?.hasSheetRuleInEffect
  })
  if (blockedByPolicy) return SHEET_BLOCKED_BY_APP_POLICY_REASON
  return sheetAlreadySubmitted.value === true ? SHEET_ALREADY_SUBMITTED_REASON : null
})

const referenceDate = computed(() => {
  const raw =
    props.context?.referenceDate ??
    originStation.value.referenceDate ??
    originStation.value.expectedStart ??
    null
  if (!raw) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw
  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) return ''
  const month = String(date.getMonth() + 1).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${String(date.getDate()).padStart(2, '0')}`
})

const formattedReferenceDate = computed(() => {
  if (!referenceDate.value) return ''
  const [year, month, day] = referenceDate.value.split('-')
  return `${day}/${month}/${year}`
})

const doctorLine = computed(() =>
  [
    originStation.value.doctorName,
    originStation.value.healthUnitName,
    originStation.value.scheduleName
  ]
    .filter(Boolean)
    .join(' · ')
)

/* ----------------------------------------------------------------------- *
 * Navegação
 * ----------------------------------------------------------------------- */

const title = computed(() =>
  currentStep.value === SEND_RECORD_STEP.CATEGORY
    ? 'Enviar registro'
    : 'Enviar registro · Registro de Atividade · Jornada'
)

const nextLabel = computed(() => {
  if (
    currentStep.value === SEND_RECORD_STEP.STATIONS &&
    selectedDoctorStationIds.value.length > 1
  ) {
    return `Próximo (${selectedDoctorStationIds.value.length} plantões)`
  }
  return 'Próximo'
})

const isNextDisabled = computed(() => {
  switch (currentStep.value) {
    case SEND_RECORD_STEP.CATEGORY:
      return category.value === null
    case SEND_RECORD_STEP.STATIONS:
      return selectedDoctorStationIds.value.length === 0
    case SEND_RECORD_STEP.TYPE_AND_FILES:
      if (!activityType.value) return true
      if (requiresAttachment(activityType.value) && files.value.length === 0) return true
      return (
        requiresDescriptionOnTypeStep(activityType.value) &&
        !isDescriptionLongEnough(description.value)
      )
    case SEND_RECORD_STEP.EQUIVALENCE:
      // A justificativa do Ajuste administrativo é obrigatória (V8 estendida).
      if (isAdminAdjustment.value) return !isDescriptionLongEnough(description.value)
      // Uma perna COBERTA sem horário viajaria como "declaro o check-in" sem check-in
      // nenhum: o backend recusaria (ACTIVITY_LEGS_INVALID) depois do upload dos anexos.
      return lines.value.some(isMissingDeclaredTime)
    default:
      return false
  }
})

/**
 * O painel abre no arquivo que ACABOU de entrar — é sobre ele que a dúvida existe.
 * O gatilho é a própria lista, e não o tamanho dela: remover um anexo e anexar
 * outro em seguida mantém o total, e o painel tem de pular para o novo mesmo assim.
 */
watch(files, (current, previous) => {
  if (!current.length) {
    attachmentIndex.value = 0
    return
  }
  const added = current.filter((file) => !previous?.includes(file))
  if (added.length) {
    attachmentIndex.value = current.indexOf(added[added.length - 1])
    return
  }
  if (attachmentIndex.value > current.length - 1) attachmentIndex.value = current.length - 1
})

function setSelected(ids: number[]): void {
  // A vaga de origem é travada: ela nunca sai da seleção.
  selectedDoctorStationIds.value = Array.from(
    new Set([resolvedDoctorStationId.value, ...ids])
  ).filter(Boolean)
}

/**
 * C7 — o Ajuste administrativo escolhido antes de voltar e somar plantões
 * deixaria de ser oferecido: melhor pedir o tipo de novo do que enviar N-1
 * plantões com valores que ninguém viu.
 */
function dropAdminAdjustmentOnMultiStation(): void {
  if (isAdminAdjustment.value && selectedDoctorStationIds.value.length > 1) {
    activityType.value = null
  }
}

async function goNext(): Promise<void> {
  if (isNextDisabled.value) return

  if (currentStep.value === SEND_RECORD_STEP.CATEGORY) {
    if (category.value === 'FOLHA') {
      // Balão ③: fecha este modal e devolve o controle ao wizard de folha.
      emit('redirect-to-sheet', {
        companyId: props.context?.companyId ?? null,
        healthUnitId: props.context?.healthUnitId ?? null,
        referenceDate: referenceDate.value,
        scheduleId: resolvedScheduleId.value,
        monitoringType: sheetMonitoringType.value ?? DSAttendanceMonitoringType.FOLHA,
        shiftId: props.context?.station?.shiftId ?? null
      })
      emit('update:modelValue', false)
      return
    }
    currentStep.value = SEND_RECORD_STEP.STATIONS
    await loadCandidates()
    return
  }

  if (currentStep.value === SEND_RECORD_STEP.STATIONS) {
    dropAdminAdjustmentOnMultiStation()
    currentStep.value = SEND_RECORD_STEP.TYPE_AND_FILES
    await loadPrefill()
    return
  }

  if (currentStep.value < SEND_RECORD_STEP.REVIEW) {
    currentStep.value += 1
  }
}

/**
 * Enter num campo de hora avança a etapa (Q6). Só nos campos de hora: em qualquer
 * outro alvo (rádio da perna, seletor de fuso) Enter continua fazendo o que o
 * navegador faz.
 */
function handleEquivalenceEnter(event: KeyboardEvent): void {
  const target = event.target
  if (!(target instanceof HTMLInputElement) || target.type !== 'time') return
  if (isNextDisabled.value) return
  event.preventDefault()
  void goNext()
}

function goBack(): void {
  errorCode.value = null
  errorMessage.value = null
  if (currentStep.value > SEND_RECORD_STEP.CATEGORY) {
    currentStep.value -= 1
    return
  }
  handleClose()
}

function handleClose(): void {
  emit('update:modelValue', false)
  setTimeout(resetModal, 300)
}

function openQueue(): void {
  emit('open-queue')
  // Sem router injetado (uso fora de rota, ou spec), quem abriu navega pelo evento.
  void router?.push({
    path: PATHS.APPROVALS_DS_ATTENDANCE,
    query: { kind: ContainerKind.ATIVIDADE }
  })
}

function resetModal(): void {
  currentStep.value = SEND_RECORD_STEP.CATEGORY
  // Folha bloqueada deixa uma categoria só: pedir um clique para escolher o que
  // não tem alternativa é só mais um passo entre a pessoa e o envio.
  category.value = sheetBlockedReason.value ? 'ATIVIDADE' : null
  sheetMonitoringType.value = null
  activitySubtype.value = 'JORNADA'
  selectedDoctorStationIds.value = resolvedDoctorStationId.value
    ? [resolvedDoctorStationId.value]
    : []
  activityType.value = null
  description.value = ''
  files.value = []
  fileErrors.value = []
  selfCheck.value = emptyActivitySelfCheckState()
  prefill.value = []
  lines.value = []
  candidateStations.value = []
  candidatesLoading.value = false
  candidatesError.value = null
  truthDeclaration.value = false
  isSaving.value = false
  errorCode.value = null
  errorMessage.value = null
  warnings.value = []
  createdRecords.value = []
  attachmentIndex.value = 0
  idempotencyKey.value = generateIdempotencyKey()
}

/* ----------------------------------------------------------------------- *
 * Pré-preenchimento
 * ----------------------------------------------------------------------- */

/**
 * "O que o sistema já tem" (§2.4). A resposta é normalizada porque o contrato
 * do backend ainda não está em produção — e `[]` (rota ausente) cai no que a
 * própria vaga já sabe, em vez de deixar o passo 4 vazio.
 */
async function loadPrefill(): Promise<void> {
  const ids = selectedDoctorStationIds.value
  let items: NormalizedPrefillItem[] = []

  try {
    const raw = await Network.dsAttendance.getActivityRecordPrefill(props.context.scheduleId, ids)
    items = (raw ?? [])
      .map((item) => normalizePrefillItem(item))
      .filter((item): item is NormalizedPrefillItem => item !== null)
  } catch (error) {
    // fallback intencional: o prefill é complementar — sem ele o passo 4 mostra o que a
    // própria vaga já sabe (`prefillFromStationOption`) e o envio segue possível; um toast
    // aqui não teria ação a oferecer para quem está enviando o registro.
    console.debug('[send-record] prefill indisponível; seguindo com o que a vaga já sabe', error)
  }

  const byId = new Map(items.map((item) => [item.doctorStationId, item]))
  const known = [originStation.value, ...otherStations.value]

  prefill.value = ids.map(
    (id) =>
      byId.get(id) ??
      prefillFromStationOption(
        known.find((option) => option.doctorStationId === id) ?? { doctorStationId: id }
      )
  )
  lines.value = prefill.value.map(buildLineFromPrefill)
}

const toleranceTexts = computed(() => {
  const byId = new Map(prefill.value.map((item) => [item.doctorStationId, item]))
  return lines.value.flatMap((line) => {
    const item = byId.get(line.doctorStationId)
    return buildToleranceWarnings({
      line,
      expectedStart: item?.expectedStart ?? null,
      expectedEnd: item?.expectedEnd ?? null,
      checkinLateToleranceMinutes:
        item?.checkinLateToleranceMinutes ?? checkinLateToleranceMinutes.value,
      checkoutEarlyToleranceMinutes:
        item?.checkoutEarlyToleranceMinutes ?? checkoutEarlyToleranceMinutes.value
    }).map((warning) => warning.text)
  })
})

/* ----------------------------------------------------------------------- *
 * Envio
 * ----------------------------------------------------------------------- */

function generateIdempotencyKey(): string {
  const uuid = globalThis.crypto?.randomUUID?.()
  return uuid ?? `send-record-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

function buildFormData(): FormData {
  const formData = new FormData()
  formData.append('scheduleId', String(resolvedScheduleId.value))
  formData.append('activityType', String(activityType.value))

  const trimmed = description.value.trim()
  if (trimmed) formData.append('activityDescription', trimmed)

  // `stations` vai como JSON string — mesmo formato de `schedules` no upload de folha.
  formData.append(
    'stations',
    JSON.stringify(
      lines.value.map((line) => ({
        doctorStationId: line.doctorStationId,
        coveredLegs: line.coveredLegs,
        // Só a perna coberta viaja: mandar a outra criaria um declarado que o
        // registro não pretende substituir (invariantes I1–I3).
        ...(line.coveredLegs !== CoveredLegs.CHECKOUT && line.declaredCheckin
          ? { declaredCheckin: line.declaredCheckin }
          : {}),
        ...(line.coveredLegs !== CoveredLegs.CHECKIN && line.declaredCheckout
          ? { declaredCheckout: line.declaredCheckout }
          : {})
      }))
    )
  )

  // `selfCheck` voltou ao multipart: o DTO do backend passou a aceitá-lo (B17 §1.1, campo
  // opcional com as 3 chaves do web, que nunca bloqueiam o envio). A I5 o tinha removido
  // porque o §2.2 não o declarava e o `forbidNonWhitelisted` derrubaria o envio em 400 —
  // premissa que deixou de valer. Vai como JSON string, igual a `stations`.
  formData.append('selfCheck', JSON.stringify(buildActivitySelfCheckPayload(selfCheck.value)))
  formData.append('idempotencyKey', idempotencyKey.value)
  files.value.forEach((file) => formData.append('files', file, file.name))
  return formData
}

/** Mapa código → texto (§2.7). Código desconhecido cai na mensagem do backend. */
function resolveError(error: unknown): { code: string | null; message: string } {
  if (isTimeoutError(error)) return { code: null, message: ACTIVITY_TIMEOUT_MESSAGE }

  const body = (error as { response?: { data?: Record<string, unknown> } })?.response?.data
  const raw = body?.code ?? body?.errorCode ?? body?.message
  const code = typeof raw === 'string' && raw in ACTIVITY_ERROR_MESSAGES ? raw : null

  return {
    code,
    message: code
      ? ACTIVITY_ERROR_MESSAGES[code]
      : getApiErrorMessage(error, 'Não foi possível enviar o registro.')
  }
}

async function submit(): Promise<void> {
  if (!truthDeclaration.value || isSaving.value) return

  isSaving.value = true
  errorCode.value = null
  errorMessage.value = null

  try {
    const response = await Network.dsAttendance.createActivityRecord(buildFormData())
    createdRecords.value = normalizeCreatedRecords(response)
    warnings.value = response?.warnings ?? []
    currentStep.value = SEND_RECORD_STEP.SUCCESS
    emit('saved', createdRecords.value)
    emit('submitted', createdRecords.value)
  } catch (error) {
    // Nada é apagado: anexos e equivalência continuam preenchidos (erro B de M2).
    const resolved = resolveError(error)
    errorCode.value = resolved.code
    errorMessage.value = resolved.message
  } finally {
    isSaving.value = false
  }
}

/**
 * `containers[]` é o contrato final (B17 §1.1): cada item diz de QUAL plantão é o
 * `#A-` criado, coisa que o `containerIds[]` posicional da W1 só conseguia adivinhar.
 *
 * `id` é o nome do contrato e `containerId` o alias que o backend ainda emite com o
 * mesmo valor (B17 §2.3); lemos os dois nesta ordem para que a remoção do alias não
 * apague a tela de sucesso. Um item sem id nenhum é descartado — melhor um registro a
 * menos na lista do que um "#A-undefined" clicável.
 */
function normalizeCreatedRecords(response: unknown): CreatedActivityRecord[] {
  const payload = (response ?? {}) as { containers?: ActivityRecordCreatedContainer[] }

  return (payload.containers ?? [])
    .map((item) => ({
      containerId: typeof item?.id === 'number' ? item.id : item?.containerId,
      doctorStationId: item?.doctorStationId ?? null,
      status: item?.status ?? 'PENDING_AUDIT'
    }))
    .filter((item): item is CreatedActivityRecord => typeof item.containerId === 'number')
}

function describeCreated(record: CreatedActivityRecord): string {
  const line = lines.value.find((item) => item.doctorStationId === record.doctorStationId)
  if (!line) return ''
  const item = prefill.value.find((entry) => entry.doctorStationId === line.doctorStationId)
  const date = item?.expectedStart ? new Date(item.expectedStart).toLocaleDateString('pt-BR') : ''
  const times = [
    isoToTimeInput(line.declaredCheckin, line.timezone),
    isoToTimeInput(line.declaredCheckout, line.timezone)
  ]
    .filter(Boolean)
    .join('–')
  return [date, times].filter(Boolean).join(' · ')
}

/**
 * O contexto chega da tela que abre o modal e pode completar DEPOIS da abertura
 * (a config da escala é uma requisição à parte). Se a folha passar a ser proibida
 * com o modal já aberto, a escolha volta para a única categoria válida — senão
 * ficaria um "FOLHA" selecionado que o passo 1 já nem mostra como clicável.
 */
watch(sheetBlockedReason, (reason) => {
  if (!reason || category.value === 'ATIVIDADE') return
  category.value = 'ATIVIDADE'
  sheetMonitoringType.value = null
})

/**
 * #3079 — a folha do turno da vaga já foi enviada? Pergunta à parte, na abertura: a
 * config de registro da escala é compartilhada e não conhece a vaga nem a data. Até
 * a resposta, ou se ela falhar, a opção segue liberada — o 409 do backend continua
 * sendo a trava. Quando a resposta chega positiva, o `watch(sheetBlockedReason)`
 * acima tira a escolha de "Folha".
 */
const SHEET_CHECK_ERROR =
  'Não foi possível verificar se a folha já foi enviada. Confira antes de enviar novamente.'
let sheetCheckId = 0
async function checkSheetAlreadySubmitted(): Promise<void> {
  const checkId = ++sheetCheckId
  sheetAlreadySubmitted.value = null

  const scheduleId = resolvedScheduleId.value
  const doctorStationId = resolvedDoctorStationId.value
  if (!scheduleId || !doctorStationId || !referenceDate.value) return

  try {
    const submitted = await Network.dsAttendance.getSheetAlreadySubmitted(
      scheduleId,
      referenceDate.value,
      doctorStationId
    )
    if (checkId === sheetCheckId) sheetAlreadySubmitted.value = submitted
  } catch (error) {
    // Avisa sem travar: a folha segue liberada e o 409 do backend continua sendo a trava,
    // mas sem o aviso o usuário preencheria tudo para só então descobrir.
    if (checkId === sheetCheckId) notifyError(error, SHEET_CHECK_ERROR)
  }
}

watch(
  () => props.modelValue,
  (isOpen) => {
    if (!isOpen) return
    resetModal()
    void checkSheetAlreadySubmitted()
  },
  { immediate: true }
)

/**
 * O catálogo só é conhecido de verdade depois do prefill. Se o Ajuste
 * administrativo deixar de ser oferecido, o tipo já escolhido cai junto — senão
 * o passo 4 abriria a variação compacta de um tipo que o backend recusaria.
 */
watch(isAdminAdjustmentOffered, (isOffered) => {
  if (!isOffered && isAdminAdjustment.value) activityType.value = null
})

defineExpose({
  currentStep,
  category,
  sheetMonitoringType,
  activitySubtype,
  selectedDoctorStationIds,
  activityType,
  description,
  files,
  fileErrors,
  selfCheck,
  prefill,
  lines,
  truthDeclaration,
  isSaving,
  errorCode,
  errorMessage,
  warnings,
  createdRecords,
  isNextDisabled,
  goNext,
  goBack,
  submit
})
</script>
